import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useCases } from "@/api/queries/cases";
import { useProcesses } from "@/api/queries/processes";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { CaseStatusBadge, StepProgress } from "@/components/ProcessBits";
import { relativeTime } from "@/lib/relativeTime";
import type { CaseStatusValue } from "@/types";

// #301 (UC-5b): die Executions-Sicht (Konzept §10) — was läuft, was hängt, was ist gelaufen.
//
// ⚠ Vorher gab es diese Seite nicht: ein gestarteter Vorgang verschwand aus der Oberfläche,
// und wer wissen wollte, was er getan hat, musste in die Datenbank sehen. Genau daran ist der
// Abriss-Versuch auf spike85 hängengeblieben.

const PAGE_SIZE = 50;
const inputCls =
  "h-9 rounded-lg border border-border-strong bg-surface px-2.5 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border";

// Reihenfolge nach Dringlichkeit, nicht alphabetisch: was jemanden braucht, steht oben.
const STATUS_OPTIONEN: Array<{ value: CaseStatusValue; label: string }> = [
  { value: "blocked", label: "blockiert" },
  { value: "waiting_approval", label: "wartet auf Freigabe" },
  { value: "waiting_input", label: "wartet auf Eingabe" },
  { value: "running", label: "läuft" },
  { value: "paused", label: "pausiert" },
  { value: "done", label: "abgeschlossen" },
  { value: "aborted", label: "abgebrochen" },
];

export function CasesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const instanceId = searchParams.get("instanceId") ?? "";
  // #303 (Review M3): der Prozess-Filter lebt in der URL wie der Instanz-Filter — die
  // Kennzahlen-Karte der Vorlage verlinkt hierher („Läufe ansehen →", `?templateId=…`),
  // und ein bloss lokaler Zustand ignorierte diesen Deep-Link vollständig: die Seite zeigte
  // ALLE Läufe und behauptete damit, das seien die der Vorlage.
  const templateId = searchParams.get("templateId") ?? "";
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  /** Filter setzen/entfernen, ohne die anderen URL-Werte zu verlieren. */
  const setzeParam = (key: string, wert: string) => {
    const naechste = new URLSearchParams(searchParams);
    if (wert) naechste.set(key, wert);
    else naechste.delete(key);
    setSearchParams(naechste, { replace: true });
    setOffset(0);
  };

  const { data, isLoading, isError, error, refetch } = useCases({
    ...(status ? { status: status as CaseStatusValue } : {}),
    ...(templateId ? { templateId } : {}),
    ...(instanceId ? { instanceId } : {}),
    limit: PAGE_SIZE,
    offset,
  });
  const templates = useProcesses();
  const rows = data?.cases ?? [];
  const total = data?.total ?? 0;
  const nameOf = (id: string) => templates.data?.find((t) => t.id === id)?.name ?? id;

  return (
    <div className="space-y-4">
      <p className="max-w-[680px] text-[13px] text-text3">
        Jeder Start eines Prozesses ist ein Lauf. Hier steht, wo er gerade ist, was ihn aufhält und
        was am Ende herauskam — Läufe bleiben erhalten, auch wenn ihre Instanz gelöscht wird.
      </p>

      <div className="flex flex-wrap items-center gap-2" data-testid="case-filters">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          className={`${inputCls} w-52`}
          data-testid="filter-status"
        >
          <option value="">Zustand: alle</option>
          {STATUS_OPTIONEN.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {/* Ein Ladefehler wird BENANNT — ein leeres Dropdown sähe aus, als gäbe es keine
            Prozesse (und wer `processes:read` nicht hat, soll den Grund sehen, nicht raten). */}
        {templates.isError ? (
          <span className="text-[12.5px] text-warn" data-testid="filter-template-fehler">
            Prozess-Liste nicht abrufbar
          </span>
        ) : (
          <select
            value={templateId}
            onChange={(e) => setzeParam("templateId", e.target.value)}
            className={`${inputCls} w-56`}
            data-testid="filter-template"
          >
            <option value="">Prozess: alle</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        {/* Aus der Instanz-Sicht verlinkt (#301 UC-5b): der Filter muss SICHTBAR sein, sonst
            sieht die Seite aus, als gäbe es nur diese paar Läufe. */}
        {instanceId && (
          <button
            type="button"
            onClick={() => setzeParam("instanceId", "")}
            className="h-9 rounded-lg border border-accent-border bg-accent-soft px-2.5 text-[12.5px] text-accent-text"
            data-testid="filter-instance"
          >
            nur eine Einrichtung · Filter aufheben ✕
          </button>
        )}
        {/* Dasselbe für den Deep-Link aus den Kennzahlen einer Vorlage (Review M3). */}
        {templateId && (
          <button
            type="button"
            onClick={() => setzeParam("templateId", "")}
            className="h-9 rounded-lg border border-accent-border bg-accent-soft px-2.5 text-[12.5px] text-accent-text"
            data-testid="filter-template-chip"
          >
            nur {nameOf(templateId)} · Filter aufheben ✕
          </button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setStatus("");
            setSearchParams({}, { replace: true });
            setOffset(0);
          }}
        >
          Zurücksetzen
        </Button>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
          Aktualisieren
        </Button>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && rows.length === 0}
        emptyMessage="Keine Läufe (für diesen Filter)"
        onRetry={() => refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Prozess</TableHead>
              <TableHead>Zustand</TableHead>
              <TableHead>Fortschritt</TableHead>
              <TableHead>Schritt</TableHead>
              <TableHead>Agent</TableHead>
              {/* #P001 (UC-4): womit der Lauf arbeitet — bei gemischten Modellen die Spalte,
                  nach der man in einer Executions-Liste als Erstes sucht. */}
              <TableHead>Modell</TableHead>
              <TableHead className="text-right">Gestartet</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid="case-row">
                <TableCell>
                  <Link to={`/cases/${c.id}`} className="text-[13px] font-semibold text-accent hover:underline">
                    {nameOf(c.templateId)}
                  </Link>
                  <div className="font-mono text-[11px] text-text3">{c.templateVersion}</div>
                </TableCell>
                <TableCell>
                  <CaseStatusBadge status={c.status} />
                </TableCell>
                <TableCell>
                  <StepProgress steps={c.steps} currentStepId={c.currentStepId} />
                </TableCell>
                <TableCell className="font-mono text-[12px] text-text2">{c.currentStepId ?? "—"}</TableCell>
                <TableCell>
                  <Link to={`/agents/${c.agentId}`} className="font-mono text-[12px] text-accent hover:underline">
                    {c.agentId}
                  </Link>
                </TableCell>
                {/* `undefined` (älterer Master) und `null` (nicht nachschlagbar) sind beide
                    „—" — nie ein erfundener Name. */}
                <TableCell className="font-mono text-[12px] text-text2">{c.model ?? "—"}</TableCell>
                <TableCell className="text-right text-[12px] text-text2">{relativeTime(c.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-[12px] text-text3">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} von {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Zurück
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Weiter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
